import { createSlice } from '@reduxjs/toolkit';
import { Order, OrderItem, OrderIncludedProduct, PaymentRequest } from '../types/order';
import { Pagination } from '../types/pagination';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  createOrderApi,
  updateOrderApi,
  getOrderByIdApi,
  getOrdersApi,
  createOrderPaymentRequestApi,
  updateOrderPaymentRequestApi,
  getOrderPaymentRequestsApi,
} from '../services/orders/orders';
import { setAlert } from './commonSlice';
import { createQuery } from '../utils/constants';
import { CurrencyInfo } from '../types/currency';

interface InitialState {
  orders: Order[];
  pagination: Pagination | null;
  prevPage: string | null;
  nextPage: string | null;
  filter: any;
  loader: boolean;
  saveLoader: boolean;
  savePaymentRequestLoader: boolean;
  selectedOrder: Order | null;
  selectedOrderIncluded: {
    products: OrderIncludedProduct[] | null;
  };
  included: {
    currencies: CurrencyInfo[] | null;
  };
  selectedOrderPaymentRequests: PaymentRequest[] | null;
}

interface Filter {
  limit: number | null;
}

const initialState: InitialState = {
  orders: [],
  pagination: null,
  prevPage: null,
  nextPage: null,
  filter: null,
  loader: false,
  saveLoader: false,
  savePaymentRequestLoader: false,
  selectedOrder: null,
  selectedOrderPaymentRequests: null,
  selectedOrderIncluded: {
    products: null,
  },
  included: {
    currencies: null,
  },
};

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    getOrders: (state, action: PayloadAction<any>) => {
      state.orders = action.payload;
    },
    setOrders: (state, action: PayloadAction<any>) => {
      state.orders = action.payload;
    },
    setPagination: (state, action: PayloadAction<Pagination>) => {
      state.pagination = action.payload;
    },
    setFilter: (state, action: PayloadAction<any>) => {
      state.filter = action.payload;
    },
    setIncluded: (state, action: PayloadAction<any>) => {
      state.included = action.payload;
    },
    setLoader: (state, action: PayloadAction<any>) => {
      state.loader = action.payload;
    },
    setSavePaymentRequestLoader: (state, action: PayloadAction<any>) => {
      state.savePaymentRequestLoader = action.payload;
    },
    setSaveLoader: (state, action: PayloadAction<any>) => {
      state.saveLoader = action.payload;
    },
    setSelectedOrder: (state, action: PayloadAction<any>) => {
      state.selectedOrder = action.payload;
    },
    setSelectedOrderPaymentRequests: (state, action: PayloadAction<any>) => {
      state.selectedOrderPaymentRequests = action.payload;
    },
    setSelectedOrderIncluded: (state, action: PayloadAction<any>) => {
      state.selectedOrderIncluded = action.payload;
    },
  },
});

export const {
  getOrders,
  setOrders,
  setPagination,
  setIncluded,
  setFilter,
  setLoader,
  setSaveLoader,
  setSavePaymentRequestLoader,
  setSelectedOrder,
  setSelectedOrderIncluded,
  setSelectedOrderPaymentRequests,
} = orderSlice.actions;
export default orderSlice.reducer;

export const getOrdersAction = (payload: any) => async (dispatch: any, getState: any) => {
  try {
    dispatch(setLoader(true));
    const { filter } = getState().order;
    const queryParams = { ...filter, ...payload };
    const query = '?currencyId=1&' + createQuery({ filter: queryParams });

    const response = await getOrdersApi(query);
    if (response.kind === 'ok') {
      const { pagination, data: list, included } = response.data;

      dispatch(setOrders(list));
      dispatch(setIncluded(included));
      dispatch(setPagination(pagination));
    }
  } catch (error) {
  } finally {
    dispatch(setLoader(false));
  }
};

export const createOrderAction = (payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await createOrderApi(payload);
    debugger;
    if (result?.kind === 'ok') {
      const { data } = result.data;
      dispatch(getOrderAction(data.id));
      dispatch(setAlert('Order has been created successfully', 'success'));
      cb?.(null, true);
    } else {
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const updateOrderAction = (id: any, payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await updateOrderApi(id, payload);
    if (result?.kind === 'ok') {
      dispatch(setAlert('Order has been updated successfully', 'success'));
      cb?.(null, true);
    } else {
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const getOrderAction = (id?: number | null) => async (dispatch: any, getState: any) => {
  if (id) {
    try {
      const response = await getOrderByIdApi(id);
      if (response.kind === 'ok') {
        const { data, included } = response.data;

        dispatch(setSelectedOrder(data));

        dispatch(setSelectedOrderIncluded(included));
      }
    } catch (error) {
    } finally {
      dispatch(setLoader(false));
    }
  } else {
    dispatch(setSelectedOrder(null));
  }
};

export const applyFilter = (payload: any) => async (dispatch: any, getState: any) => {
  return dispatch(setFilter(payload));
};

export const createOrderPaymentReqeustAction = (id: number, payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSavePaymentRequestLoader(true));
    const result = await createOrderPaymentRequestApi(id, payload);
    debugger;
    if (result?.kind === 'ok') {
      const { data } = result.data;
      dispatch(getOrderPaymentReuestsAction(data.id));
      dispatch(setAlert('Payment request has been created successfully', 'success'));
      cb?.(null, true);
    } else {
    }
  } catch (err) {
  } finally {
    dispatch(setSavePaymentRequestLoader(false));
  }
};

export const updateOrderPaymentReqeustAction =
  (id: any, paymentRequestId: number, payload: any, cb?: any) => async (dispatch: any) => {
    try {
      dispatch(setSavePaymentRequestLoader(true));
      const result = await updateOrderPaymentRequestApi(id, paymentRequestId, payload);
      if (result?.kind === 'ok') {
        dispatch(getOrderPaymentReuestsAction(id));
        dispatch(setAlert('Payment request has been updated successfully', 'success'));
        cb?.(null, true);
      } else {
      }
    } catch (err) {
    } finally {
      dispatch(setSavePaymentRequestLoader(false));
    }
  };

export const getOrderPaymentReuestsAction = (id?: number | null) => async (dispatch: any, getState: any) => {
  if (id) {
    try {
      const response = await getOrderPaymentRequestsApi(id);
      if (response.kind === 'ok') {
        const { data } = response.data;

        dispatch(setSelectedOrderPaymentRequests(data));
      }
    } catch (error) {
    } finally {
      dispatch(setLoader(false));
    }
  } else {
    dispatch(setSelectedOrderPaymentRequests(null));
  }
};
